import { useState, useCallback, useEffect } from 'react';
import { asperaSdkSingleton, asperaSingleton } from '@warehouse/object-browser/infra';
import { useSnackbar } from 'notistack';

interface Installer {
	platform: string;
	type: string;
	url: string;
}

interface InstallerInfo {
	entries?: Installer[];
}

interface AsperaError {
	debugData?: {
		message?: string;
	};
	message?: string;
}

interface AsperaFile {
	name?: string;
}

interface File {
	type: string;
	name: string;
}

const truncateText = (text: string | undefined, maxLength: number = 20): string => {
	if (!text) return '';
	return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export function useAsperaUpload({
	currentBucketPath,
	bucketInfo,
}: {
	currentBucketPath: string;
	bucketInfo: { id: string };
}) {
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [warning, setWarning] = useState<string | null>(null);
	const [transferId, setTransferId] = useState<string | null>(null);
	const [transferStatus, setTransferStatus] = useState<any>(null);
	const [showFallback, setShowFallback] = useState(false);
	const [installers, setInstallers] = useState<Installer[]>([]);
	const [uploadedFile, setUploadedFile] = useState<File | null>({ type: '', name: '' });
	const [pendingUploadType, setPendingUploadType] = useState<boolean | null>(null);
	const asperaRepository = asperaSingleton.get();
	const asperaSdk = asperaSdkSingleton.get();
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		if (error) {
			enqueueSnackbar(error, { variant: 'error' });
		} else if (success) {
			enqueueSnackbar(success, { variant: 'success' });
		} else if (warning) {
			enqueueSnackbar(warning, { variant: 'info' });
		}
	}, [error, success, warning, enqueueSnackbar]);

	const sdk = asperaSdk;
	const handleUpload = useCallback(
		async (uploadType: boolean) => {
			if (!sdk || typeof sdk.init !== 'function') {
				setError('Aspera SDK failed to load.');
				setShowFallback(true);
				return;
			}
			try {
				await sdk.testConnection();
				setShowFallback(false);
			} catch (testErr) {
				setPendingUploadType(uploadType);
				setShowFallback(true);
				sdk?.getInstallerInfo({}).then((info: InstallerInfo) => {
					setInstallers(info.entries || []);
					setShowFallback(true);
				});
				setWarning('Aspera Connect needs to be launched. Please click "Launch Aspera Connect" below.');
				return;
			}
			setIsUploading(false);
			setError(null);
			setSuccess(null);
			setWarning(null);
			setTransferStatus(null);
			setTransferId(null);

			try {
				await sdk.getInstallerInfo({}).then((info: InstallerInfo) => {
					setInstallers(info.entries || []);
					setShowFallback(true);
				});

				await sdk.init({ appId: 'warehouse-webui', supportMultipleUsers: true }).catch((err: unknown) => {
					const asperaErr = err as AsperaError;
					const errorMessage = `${asperaErr?.debugData?.message || asperaErr?.message || 'An error occurred while selecting files.'}`;

					if (errorMessage) {
						setShowFallback(false);
						setError(errorMessage);
						setWarning(`Aspera SDK is now launching: Please try selecting files or folders again.`);
					}
				});

				await sdk.testConnection().then(() => {
					setShowFallback(false);
				});

				let response;
				try {
					response = uploadType ? await sdk.showSelectFolderDialog() : await sdk.showSelectFileDialog();
				} catch (err: unknown) {
					setIsUploading(false);
					return;
				}
				const { files } = response?.dataTransfer || {};
				if (!files || files.length === 0) {
					setIsUploading(false);
					return;
				}

				const paths = files.map((f: AsperaFile) => {
					const pathParts = f.name?.split('/') || [];
					const fileName = pathParts[pathParts.length - 1] || '';

					return {
						source: f.name || '',
						destination: fileName,
					};
				});

				setUploadedFile({
					type: `${uploadType ? 'Folder' : 'File'}` || '',
					name: `${paths[0]?.destination}`,
				});
				// Use repository to get transfer spec
				const transferSpec = await asperaRepository.asperaTransferSpec({
					paths,
					destinationRoot: currentBucketPath || '',
					bucket: bucketInfo?.id,
				});

				// Fix direction type for Aspera SDK
				let direction: 'send' | 'receive' | undefined = 'send';
				if (transferSpec.direction === 'send' || transferSpec.direction === 'receive') {
					direction = transferSpec.direction;
				}
				const sdkTransferSpec = { ...transferSpec, direction };
				const transfer = await sdk.startTransfer(sdkTransferSpec, { use_absolute_destination_path: false });
				if (transfer?.uuid) {
					setTransferId(transfer.uuid);
				}
			} catch (err: unknown) {
				const asperaErr = err as AsperaError;
				const errorMessage = `${asperaErr?.debugData?.message || asperaErr?.message || 'An error occurred while selecting files.'}`;
				setError(errorMessage);
			} finally {
				setIsUploading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentBucketPath, asperaRepository, bucketInfo],
	);

	const getTransferStatus = useCallback(async () => {
		if (!transferId) return;
		try {
			const response = await sdk.getTransfer(transferId);
			setTransferStatus(response?.status);
			if (response?.status === 'completed') {
				const displayName = truncateText(uploadedFile?.name);
				setSuccess(`${uploadedFile?.type} : ${displayName} is uploaded successfully!`);
			} else if (response?.status === 'queued') {
				setWarning('Upload started');
			}
		} catch (errGet: unknown) {
			const asperaErr = errGet as AsperaError;
			const errGetMessage = `${asperaErr?.debugData?.message || asperaErr?.message || 'An error occurred while selecting files.'}`;
			setError(`${errGetMessage}`);
		}
	}, [sdk, transferId, uploadedFile?.name, uploadedFile?.type]);

	const handleCloseFallback = () => setShowFallback(!showFallback);

	const handleLaunch = () => {
		if (sdk?.launch) sdk.launch();
		setShowFallback(false);

		if (pendingUploadType !== null) {
			setTimeout(() => {
				handleUpload(pendingUploadType);
				setPendingUploadType(null);
			}, 3000);
		}
	};

	return {
		isUploading,
		error,
		success,
		warning,
		showFallback,
		installers,
		transferId,
		transferStatus,
		handleCloseFallback,
		handleLaunch,
		getTransferStatus,
		handleUpload,
	};
}
